import { useEffect, useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

const SKIP_VALUE = "__none__";
const PREVIEW_ROW_LIMIT = 25;

type LeadImportField = "firstName" | "lastName" | "email" | "phoneNumber";

type ColumnMapping = Record<LeadImportField, number | null>;

type ParsedCsv = {
  fileName: string;
  headers: string[];
  rows: string[][];
};

const LEAD_FIELDS: { key: LeadImportField; label: string }[] = [
  { key: "firstName", label: "First name" },
  { key: "lastName", label: "Last name" },
  { key: "email", label: "Email" },
  { key: "phoneNumber", label: "Phone number" },
];

const FIELD_ALIASES: Record<LeadImportField, string[]> = {
  firstName: ["first name", "firstname", "first", "fname", "given name", "name"],
  lastName: ["last name", "lastname", "last", "lname", "surname", "family name"],
  email: ["email", "e mail", "email address", "e-mail"],
  phoneNumber: [
    "phone",
    "phone number",
    "phonenumber",
    "mobile",
    "cell",
    "telephone",
    "tel",
  ],
};

function normalizeHeader(header: string): string {
  return header.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
}

function guessColumnIndex({
  headers,
  field,
  usedIndexes,
}: {
  headers: string[];
  field: LeadImportField;
  usedIndexes: Set<number>;
}): number | null {
  const normalizedHeaders = headers.map((header) => normalizeHeader(header));
  const aliases = FIELD_ALIASES[field];

  for (const alias of aliases) {
    const index = normalizedHeaders.findIndex(
      (header, i) => header === alias && !usedIndexes.has(i),
    );
    if (index >= 0) return index;
  }

  return null;
}

function guessColumnMapping(headers: string[]): ColumnMapping {
  const usedIndexes = new Set<number>();
  const mapping: ColumnMapping = {
    firstName: null,
    lastName: null,
    email: null,
    phoneNumber: null,
  };

  for (const field of LEAD_FIELDS) {
    const index = guessColumnIndex({ headers, field: field.key, usedIndexes });
    mapping[field.key] = index;
    if (index != null) usedIndexes.add(index);
  }

  return mapping;
}

function parseCsvFile({ file, text }: { file: File; text: string }): ParsedCsv | null {
  const parsed = Papa.parse<string[]>(text, { skipEmptyLines: "greedy" });
  const rows = parsed.data.filter((row) => row.some((cell) => cell?.trim()));
  if (rows.length === 0) return null;

  const headerRow = [...(rows[0] ?? [])];
  if (headerRow[0]) headerRow[0] = headerRow[0].replace(/^\uFEFF/, "");

  const headers = headerRow.map((header, index) => header.trim() || `Column ${index + 1}`);
  const dataRows = rows.slice(1).map((row) => headers.map((_, index) => row[index] ?? ""));

  return { fileName: file.name, headers, rows: dataRows };
}

function cellValue({
  row,
  columnIndex,
}: {
  row: string[];
  columnIndex: number | null;
}): string {
  if (columnIndex == null) return "";
  return (row[columnIndex] ?? "").trim();
}

export function ContactsImport() {
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const inputRef = useRef<HTMLInputElement>(null);
  const [csv, setCsv] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({
    firstName: null,
    lastName: null,
    email: null,
    phoneNumber: null,
  });
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Contacts", path: "/contacts" },
      { label: "Import", path: "/contacts/import" },
    ]);
    return () => clearBreadcrumbs();
  }, [setBreadcrumbs, clearBreadcrumbs]);

  const mappedColumnIndexes = useMemo(() => {
    return new Set(
      Object.values(mapping).filter((index): index is number => index != null),
    );
  }, [mapping]);

  const importableCount = useMemo(() => {
    if (!csv) return 0;
    return csv.rows.filter((row) => {
      const email = cellValue({ row, columnIndex: mapping.email });
      const phoneNumber = cellValue({ row, columnIndex: mapping.phoneNumber });
      return Boolean(email || phoneNumber);
    }).length;
  }, [csv, mapping]);

  const previewRows = csv?.rows.slice(0, PREVIEW_ROW_LIMIT) ?? [];

  const handleUpload = async (file: File) => {
    setIsParsing(true);
    try {
      const text = await file.text();
      const parsed = parseCsvFile({ file, text });
      if (!parsed) {
        toast.error("That CSV file is empty.");
        return;
      }
      if (parsed.rows.length === 0) {
        toast.error("That CSV has headers but no contact rows.");
        return;
      }
      setCsv(parsed);
      setMapping(guessColumnMapping(parsed.headers));
    } catch (error) {
      console.error("Error parsing contacts CSV:", error);
      toast.error("Could not read that CSV file.");
    } finally {
      setIsParsing(false);
    }
  };

  const handleClear = () => {
    setCsv(null);
    setMapping({
      firstName: null,
      lastName: null,
      email: null,
      phoneNumber: null,
    });
  };

  const handleMappingChange = ({
    field,
    value,
  }: {
    field: LeadImportField;
    value: string;
  }) => {
    setMapping((prev) => ({
      ...prev,
      [field]: value === SKIP_VALUE ? null : Number(value),
    }));
  };

  const handleImport = async () => {
    if (!csv) return;
    if (mapping.email == null && mapping.phoneNumber == null) {
      toast.error("Map at least Email or Phone number so contacts can be matched.");
      return;
    }

    const contacts = csv.rows.map((row) => ({
      firstName: cellValue({ row, columnIndex: mapping.firstName }),
      lastName: cellValue({ row, columnIndex: mapping.lastName }),
      email: cellValue({ row, columnIndex: mapping.email }),
      phoneNumber: cellValue({ row, columnIndex: mapping.phoneNumber }),
    }));

    setIsImporting(true);
    try {
      const response = await fetch("/api/contacts/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contacts }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to import contacts");
      }

      toast.success(
        `Imported ${data.createdCount.toLocaleString()} new contact${data.createdCount === 1 ? "" : "s"}` +
          `, updated ${data.updatedCount.toLocaleString()}` +
          (data.unchangedCount
            ? `, ${data.unchangedCount.toLocaleString()} already complete`
            : "") +
          (data.skippedCount
            ? `, skipped ${data.skippedCount.toLocaleString()} without email or phone`
            : "") +
          ".",
      );
    } catch (error) {
      console.error("Contacts import error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to import contacts");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="p-4 max-w-[min(100vw-2rem,1400px)] mx-auto w-full space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Import contacts</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Upload a CSV, map columns to contact fields, then import. Existing contacts
          matched by email or phone only get fields they are missing.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>CSV file</CardTitle>
          <CardDescription>
            The first row should contain column headers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleUpload(file);
              e.target.value = "";
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isParsing || isImporting}
              onClick={() => inputRef.current?.click()}
            >
              {isParsing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Parsing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {csv ? "Replace CSV" : "Upload CSV"}
                </>
              )}
            </Button>
            {csv ? (
              <Button
                type="button"
                variant="ghost"
                disabled={isImporting}
                onClick={handleClear}
              >
                <X className="h-4 w-4 mr-2" />
                Clear
              </Button>
            ) : null}
            {csv ? (
              <p className="text-sm text-muted-foreground">
                {csv.fileName} · {csv.rows.length.toLocaleString()} row
                {csv.rows.length !== 1 ? "s" : ""}
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {csv ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Column mapping</CardTitle>
              <CardDescription>
                Choose which CSV column maps to each contact field. Email or phone is
                required to create or match a contact.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {LEAD_FIELDS.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={`map-${field.key}`}>{field.label}</Label>
                    <Select
                      value={
                        mapping[field.key] == null
                          ? SKIP_VALUE
                          : String(mapping[field.key])
                      }
                      onValueChange={(value) =>
                        handleMappingChange({ field: field.key, value })
                      }
                    >
                      <SelectTrigger id={`map-${field.key}`} className="w-full">
                        <SelectValue placeholder="Not mapped" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SKIP_VALUE}>Not mapped</SelectItem>
                        {csv.headers.map((header, index) => (
                          <SelectItem key={`${header}-${index}`} value={String(index)}>
                            {header}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
              <CardDescription>
                Showing {previewRows.length.toLocaleString()} of{" "}
                {csv.rows.length.toLocaleString()} row{csv.rows.length !== 1 ? "s" : ""}.
                Mapped columns are highlighted.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {csv.headers.map((header, index) => (
                        <TableHead
                          key={`${header}-${index}`}
                          className={
                            mappedColumnIndexes.has(index) ? "bg-muted/70" : undefined
                          }
                        >
                          {header}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRows.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {csv.headers.map((_, columnIndex) => (
                          <TableCell
                            key={columnIndex}
                            className={
                              mappedColumnIndexes.has(columnIndex)
                                ? "bg-muted/40"
                                : undefined
                            }
                          >
                            {row[columnIndex] || "—"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {importableCount.toLocaleString()} contact
              {importableCount !== 1 ? "s" : ""} have an email or phone number
            </p>
            <Button
              type="button"
              disabled={isImporting || importableCount === 0}
              onClick={() => void handleImport()}
            >
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                "Import contacts"
              )}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
